import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../api/paths";
import DataTable from "../../components/data-table/DataTable";
import SearchIcon from "../../components/icons/SearchIcon";
import Pagination from "../../components/pagination/Pagination";
import useData from "../../hooks/use-data";
import usePagination from "../../hooks/use-pagination";
import { ClaimDetailsType } from "../../types/claim";
import { PaginatedApiResponseType } from "../../types/common";
import { ActionType, ColumnType } from "../../types/data-table";
import MyClaimsStats from "./MyClaimsStats/MyClaimsStats";
import DynamicTabs from "../../components/tab/DynamicTabs";
import { BsArrowUpRight } from "react-icons/bs";
import CustomButton from "../../components/custom-button/CustomButton";
import NautilusAsset from "../purchase-cover/nautilus-asset-page/NautilusAsset";
import useDisclosure from "../../hooks/use-disclosure";
import { useAppContext } from "../../context/app-context";

export default function MyClaims() {
  const { t } = useTranslation(["myClaims", "common"]);
  const { keycloakProfile } = useAppContext();
  const navigate = useNavigate();
  const pagination = usePagination();
  const [searchInput, setSearchInput] = useState("");
  const [searchParams, setSearchParams] = useSearchParams("");
  const [activeTab, setActiveTab] = useState<number>(1);
  const { search } = Object.fromEntries(searchParams);
  const { onHide, onShow, show } = useDisclosure();
  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        email: keycloakProfile?.email,
        search,
      },
    }),
    [pagination.page, pagination.limit, search]
  );
  const { data, loading } = useData<
    PaginatedApiResponseType<ClaimDetailsType[]>
  >(ApiPaths.claims, {
    options: params,
    onError: () => {
      toast(t("details.error.fetch_table", { ns: "myClaims" }), {
        type: "error",
      });
    },
    fetchIf: !!keycloakProfile?.email,
  });

  const handleFilter = (key: string, value: string) => {
    setSearchParams({ ...Object.entries(searchParams), [key]: value });
  };

  const handleDownload = (
    base64String: string,
    fileName: string = "file.pdf"
  ) => {
    try {
      // Decode base64 string to raw binary data
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers: number[] = Array.from({
        length: byteCharacters.length,
      });
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.append(link);
      link.click();
      link.remove();

      // Clean up the object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download the file. Please try again.");
      console.error("Download error:", error);
    }
  };

  const columns: ColumnType[] = [
    {
      accessor: "cover.product.product_name",
      header: t("table_head.product_name", { ns: "myClaims" }),
      formatDisplay: (row) => {
        return row?.cover?.product?.product_name ? (
          <div>{row?.cover?.product?.product_name}</div>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover.product.product_owner_name",
      header: t("table_head.product_owner_name", { ns: "myClaims" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover.product.product_type",
      header: t("table_head.product_type", { ns: "myClaims" }),
      formatDisplay: (row) => {
        return (
          <div>
            {row?.cover?.product?.product_type
              ?.map((item: any) => item?.label)
              .join(", ")}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover.product.cover_wording",
      header: t("table_head.product_details", {
        ns: "myCovers",
      }),
      formatDisplay: (row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(row?.cover?.product?.cover_wording);
          }}
          // target="_blank"
          rel="noreferrer"
        >
          Download <BsArrowUpRight />
        </a>
      ),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "status",
      header: t("table_head.status", { ns: "myClaims" }),
      formatDisplay: (row) => {
        if (!row.status) {
          return <div>-</div>;
        }
        return (
          <button
            className={`status-button px-4 py-2 ${row.status.toLowerCase()}-status`}
          >
            {row.status}
          </button>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actions: ActionType[] = [
    {
      name: "raise_new_claim",
      action: (row) => {
        return (
          <CustomButton
            className="primary-button"
            onClick={() => {
              navigate(`${row._id}`);
            }}
          >
            {t("button.view_details", { ns: "myClaims" })}
          </CustomButton>
        );
      },
    },
  ];

  const OnChainCovers = () => {
    return (
      <div className="mt-4">
        <DataTable
          columns={columns}
          data={data?.data}
          showSerial={true}
          actions={actions}
          headerBackground={"bg-dark text-white"}
          loading={loading}
        />
        <div className="mb-5">
          <Pagination total={data?.count ?? 10} pagination={pagination} />
        </div>
      </div>
    );
  };

  const tabs = [
    {
      id: 1,
      title: t("cart.traditional_insurance", { ns: "components" }),
      component: <OnChainCovers />,
    },
    {
      id: 2,
      title: t("cart.nautilus_asset_covers", { ns: "components" }),
      component: (
        <NautilusAsset
          onHide={onHide}
          show={show}
          redirectURL="https://app.nautilusfinance.io/"
        />
      ),
    },
  ];

  const handleTabChange = (selectedTab: number) => {
    setActiveTab(selectedTab);
    if (+selectedTab === 2) {
      onShow();
    }
  };

  return (
    <div className="p-3 pb-5 p-md-4">
      <div className="rounded-2 bg-white p-4 pb-0 px-md-5 pb-md-3 mb-3 mb-md-4 box-shadow">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <h2 className="primary-title mb-3 mb-md-0">
            {t("heading", { ns: "myClaims" })}
          </h2>
          <div className="primary-input-group">
            <input
              type="search"
              className="primary-search-input"
              placeholder={
                t("placeholder.search", { ns: "myClaims" }) as string
              }
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              className="primary-button search-btn"
              type="button"
              onClick={() => handleFilter("search", searchInput)}
            >
              {t("button.search", { ns: "common" })}
            </button>
            <div className="d-md-none search-icon">
              <SearchIcon />
            </div>
          </div>
        </div>
        <MyClaimsStats />
      </div>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <Link
          to="/my-covers"
          className="primary-button buy-covers-button text-decoration-none"
        >
          {t("button.raise", { ns: "myClaims" })}
        </Link>
      </div>
      <DynamicTabs
        tabs={tabs}
        currentTab={activeTab}
        currentSelectedTab={handleTabChange}
      />
    </div>
  );
}
